<template>
  <div ref="movableElement" class="c-movable-widget fixed" :class="{ 'cursor-move': !dragHandle }" :style="style">
    <slot />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { useDraggable, useLocalStorage } from '@vueuse/core'

const props = withDefaults(defineProps<{ localStorageKey?: string; isVisible?: boolean }>(), {
  localStorageKey: 'c_movable_widget_position',
  isVisible: false,
})
const movableElement = ref<HTMLElement | null>(null)
const dragHandle = ref<HTMLElement | null>(null)
const draggableElement = ref<HTMLElement | null>(null)
const { x, y, isDragging } = useDraggable(draggableElement)
const defaultPosition = { top: 0, left: 0 }
const savedPosition = useLocalStorage(props.localStorageKey, defaultPosition, {
  deep: true,
  listenToStorageChanges: true,
})
const style = computed(() => ({
  top: y.value ? y.value + 'px' : 0,
  left: x.value ? x.value + 'px' : 0,
}))

function isPositionWithinBounds(element: HTMLElement | null = null, position: { top: number; left: number }) {
  const rect = element?.getBoundingClientRect()
  if (!rect) return false
  const withinWidth = position.left >= 0 && position.left <= window.innerWidth - rect.width
  const withinHeight = position.top >= 0 && position.top <= window.innerHeight - rect.height
  return withinWidth && withinHeight
}

watch(isDragging, (dragging) => {
  if (!dragging) {
    savedPosition.value = { top: y.value, left: x.value }
  }
})

// update active position if localstorage value changed
watch(
  savedPosition,
  (position) => {
    if (!position) return
    if (position.left && position.left !== x.value) x.value = position.left
    if (position.top && position.top !== y.value) y.value = position.top
  },
  { immediate: true },
)

// make sure the widget is within the bounds of the window on appear
watch(
  () => props.isVisible,
  (isVisible, wasVisible) => {
    if (isVisible && !wasVisible && movableElement.value) {
      // if dragHandle is not provided, use the whole element as the draggable area
      dragHandle.value = movableElement.value.querySelector('.drag-handle')
      draggableElement.value = dragHandle.value || movableElement.value
      // handle positioning
      const position = savedPosition.value
      if (!isPositionWithinBounds(movableElement.value, position)) {
        savedPosition.value = defaultPosition
        x.value = defaultPosition.left
        y.value = defaultPosition.top
      } else {
        x.value = position.left
        y.value = position.top
      }
    }
  },
  { immediate: true },
)
</script>

<style lang="scss" scoped>
.c-movable-widget {
  box-shadow: 4px 2px 24px 0px rgba(73, 68, 68, 0.1);
}
</style>
